import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * Reactivate landing page — fires GET /api/auth/reactivate?token=... on mount.
 * On success, the workshop flips from read-only back to active.
 */
export default function Reactivate() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // "checking" | "ok" | "already" | "failed" | "deleted"
  const [err, setErr] = useState("");
  const [garageName, setGarageName] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) {
        if (alive) { setState("failed"); setErr("Missing reactivation token."); }
        return;
      }
      try {
        const { data } = await api.get(`/auth/reactivate?token=${encodeURIComponent(token)}`);
        if (!alive) return;
        if (data.already_active) setState("already");
        else if (data.reactivated) setState("ok");
        else setState("failed");
        setGarageName(data?.garage?.name || "");
      } catch (e) {
        if (!alive) return;
        const status = e?.response?.status;
        setErr(e?.response?.data?.detail || "This reactivation link is invalid or has expired.");
        setState(status === 410 ? "deleted" : "failed");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#f4f6f8] grid place-items-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#e6edf1] shadow-sm p-6 sm:p-8 text-center" data-testid="reactivate-page">
        {state === "checking" && (
          <>
            <Loader2 size={28} className="animate-spin mx-auto text-[#457b9d] mb-4" />
            <h1 className="text-[18px] font-bold text-[#1d3557]">Reactivating your workshop…</h1>
          </>
        )}
        {(state === "ok" || state === "already") && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#22c55e]/15 text-[#16a34a] grid place-items-center mx-auto mb-4">
              <CheckCircle2 size={28} />
            </div>
            <h1 className="text-[20px] font-bold text-[#1d3557]" data-testid="reactivate-success">
              {state === "already" ? "Workshop already active" : "Workshop reactivated"}
            </h1>
            <p className="text-sm text-[#667085] leading-relaxed mt-2">
              {state === "already"
                ? "Your workshop is already active and editable — no action needed."
                : (<>Welcome back{garageName ? <>, <b>{garageName}</b></> : ""} — your team can start creating job cards, estimates and invoices right away.</>)}
            </p>
            <Button
              onClick={() => nav("/login")}
              className="mt-6 bg-[#e63946] hover:bg-[#d02f3c] text-white"
              data-testid="reactivate-goto-login"
            >
              Log in <ArrowRight size={14} className="ml-1" />
            </Button>
          </>
        )}
        {state === "failed" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#e63946]/12 text-[#e63946] grid place-items-center mx-auto mb-4">
              <XCircle size={28} />
            </div>
            <h1 className="text-[20px] font-bold text-[#1d3557]" data-testid="reactivate-failed">
              Couldn&apos;t reactivate
            </h1>
            <p className="text-sm text-[#667085] leading-relaxed mt-2">{err}</p>
            <div className="flex items-start gap-2 p-3 mt-4 rounded-md bg-[#F79009]/10 text-[#B54708] text-left">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div className="text-xs leading-relaxed">
                If your last reactivation email is old, log in — you&apos;ll receive a fresh one automatically.
              </div>
            </div>
            <Link to="/login" className="inline-block mt-6 text-sm text-[#e63946] font-semibold" data-testid="reactivate-goto-login-failed">
              Back to login
            </Link>
          </>
        )}
        {state === "deleted" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#e63946]/12 text-[#e63946] grid place-items-center mx-auto mb-4">
              <XCircle size={28} />
            </div>
            <h1 className="text-[20px] font-bold text-[#1d3557]" data-testid="reactivate-deleted">
              Workshop has been deactivated
            </h1>
            <p className="text-sm text-[#667085] leading-relaxed mt-2">
              Your workshop has been marked for deletion. You can still recover
              it for a short window by contacting support.
            </p>
            <a
              href="mailto:contact@fenderapp.com?subject=Restore%20workshop"
              className="inline-block mt-6 h-10 leading-10 px-4 rounded-md bg-[#1d3557] hover:bg-[#152841] text-white text-sm font-semibold"
              data-testid="reactivate-contact-support"
            >
              Contact support
            </a>
          </>
        )}
      </div>
    </div>
  );
}
